/**
 * React hook for checking subscription status and redirecting unpaid users.
 * Used by dashboard pages to enforce billing gate.
 *
 * Now handles:
 * - Active paid subscriptions → allow access
 * - Active trials → allow access
 * - Expired trials → redirect to onboarding for upgrade
 * - No subscription → redirect to onboarding
 */

import { useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import {
  shouldRedirectToOnboarding,
  isTrialExpired,
  getTrialDaysRemaining,
} from '@/lib/utils/subscription';
import {
  PENDING_SESSION_MARKER_PARAM,
  PENDING_SESSION_MARKER_VALUE,
} from '@/lib/auth/session-marker';

/**
 * Hook to check subscription status and redirect unpaid users to onboarding.
 * Should be used in dashboard pages that require paid subscription or active trial.
 *
 * @param options - Configuration options
 * @param options.redirectOnUnpaid - Whether to redirect unpaid users (default: true)
 * @param options.showToast - Whether to show toast notification (default: true)
 * @param options.enabled - Whether the subscription check should run (default: true)
 */
export function useSubscriptionCheck(
  options: {
    redirectOnUnpaid?: boolean;
    showToast?: boolean;
    enabled?: boolean;
  } = {}
) {
  const { user, loading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const { toast } = useToast();
  const { redirectOnUnpaid = true, showToast = true, enabled = true } = options;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Safety check: Don't run subscription check on onboarding/checkout pages
    const isBillingFlow = pathname?.startsWith('/onboarding') || pathname?.startsWith('/checkout');
    if (isBillingFlow) {
      return;
    }

    // Wait until auth state is resolved to avoid false redirects on page refresh
    if (loading) {
      return;
    }

    const hasSessionToken =
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get(PENDING_SESSION_MARKER_PARAM) ===
        PENDING_SESSION_MARKER_VALUE;

    // Use user data from auth context (no API call needed)
    if (!user) {
      // If we're in the middle of an OAuth session exchange, let it finish
      if (hasSessionToken) {
        return;
      }

      router.push('/login');
      return;
    }

    if (shouldRedirectToOnboarding(user)) {
      // Determine the appropriate message based on trial status
      let title = 'Subscription Required';
      let description = 'Start a free trial or add a payment method to access this feature.';

      if (isTrialExpired(user)) {
        title = 'Trial Expired';
        description = 'Your 14-day free trial has ended. Please add a payment method to continue.';
      }

      if (showToast) {
        toast({
          title,
          description,
          variant: 'default',
        });
      }
      if (redirectOnUnpaid) {
        router.push('/onboarding');
      }
    }
  }, [user, loading, router, toast, redirectOnUnpaid, showToast, enabled, pathname]);

  // Return trial info for components that want to display it
  return {
    isTrialExpired: user ? isTrialExpired(user) : false,
    trialDaysRemaining: user ? getTrialDaysRemaining(user) : 0,
  };
}
